import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  ReactElement,
  Dispatch,
  SetStateAction,
  MouseEvent as ReactMouseEvent,
  PropsWithChildren,
} from 'react';

type Position = 'bottom-center' | 'bottom-left' | 'bottom-right';

const defaultRect = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
};

type Rect = Pick<DOMRect, 'left' | 'top' | 'height' | 'width'>;

const PopoverContext = React.createContext<{
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  preferredPosition: Position;
  triggerRect: Rect;
  setTriggerRect: Dispatch<SetStateAction<Rect>>;
}>({
  isShow: false,
  setIsShow: () => {
    throw new Error('PopoverContext must be used inside a provider');
  },
  preferredPosition: 'bottom-center',
  triggerRect: defaultRect,
  setTriggerRect: () => {
    throw new Error('PopoverContext must be used inside a provider');
  },
});

export default function Popover({
                                  children,
                                  preferredPosition = 'bottom-center',
                                }: PropsWithChildren<{ preferredPosition?: Position }>) {
  const [isShow, setIsShow] = useState(false);
  const [triggerRect, setTriggerRect] = useState<Rect>(defaultRect);

  return (
      <PopoverContext.Provider
          value={{ isShow, setIsShow, preferredPosition, triggerRect, setTriggerRect }}
      >
        {children}
      </PopoverContext.Provider>
  );
}

function Trigger({ children }: { children: ReactElement<any> }) {
  const { setIsShow, setTriggerRect } = useContext(PopoverContext);
  const ref = useRef<HTMLElement | null>(null);

  const onClick = () => {
    const element = ref.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    setTriggerRect(rect);
    setIsShow(prev => !prev);
  };

  return React.cloneElement(children, {
    onClick,
    ref,
  } as Partial<typeof children.props>);
}

function Close({ children }: { children: ReactElement<any> }) {
  const { setIsShow } = useContext(PopoverContext);
  const onClick = (e: ReactMouseEvent) => {
    e.stopPropagation();
    setIsShow(false);
  };

  return React.cloneElement(children, {
    onClick,
  } as Partial<typeof children.props>);
}

function Content({ children }: PropsWithChildren<{}>) {
  const { isShow } = useContext(PopoverContext);
  if (!isShow) return null;

  return <ContentInternal>{children}</ContentInternal>;
}

function ContentInternal({ children }: PropsWithChildren<{}>) {
  const { triggerRect, preferredPosition, setIsShow } = useContext(PopoverContext);
  const ref = useRef<HTMLDialogElement | null>(null);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    setCoords(getPopoverCoords(triggerRect, rect, preferredPosition));
  }, [triggerRect, preferredPosition]);

  const refFocus = useFocusTrapping();
  const refClickOutside = useClickOutside(() => setIsShow(false));
  const mergedRef = mergeRefs(ref, refFocus, refClickOutside);

  return (
      <dialog
          open
          ref={mergedRef}
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            margin: 0,
            zIndex: 1000,
          }}
      >
        {children}
      </dialog>
  );
}

Popover.Trigger = Trigger;
Popover.Content = Content;
Popover.Close = Close;

// -- UTILS & HOOKS --

function getPopoverCoords(trigger: Rect, popover: Rect, position: Position) {
  let top = trigger.top + trigger.height + 10;
  let left = trigger.left + trigger.width / 2 - popover.width / 2;

  if (top + popover.height > window.innerHeight - 10) {
    top = trigger.top - popover.height - 10;
  }

  return {
    top: Math.max(top, 10),
    left: Math.max(left, 10),
  };
}

const focusableQuery = 'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

function useFocusTrapping() {
  const refTrigger = useRef<HTMLElement | null>(document.activeElement as HTMLElement);
  const ref = useRef<HTMLElement | null>(null);

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const popover = ref.current;
    if (!popover) return;

    const focusables = Array.from(
        popover.querySelectorAll<HTMLElement>(focusableQuery)
    ).filter(el => !el.hasAttribute('disabled'));

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (document.activeElement === last && !e.shiftKey) {
      e.preventDefault();
      first.focus();
    } else if (document.activeElement === first && e.shiftKey) {
      e.preventDefault();
      last.focus();
    }
  }, []);

  useEffect(() => {
    const popover = ref.current;
    if (!popover) return;

    const focusables = Array.from(
        popover.querySelectorAll<HTMLElement>(focusableQuery)
    );
    focusables[0]?.focus();

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (document.activeElement === document.body) {
        refTrigger.current?.focus();
      }
    };
  }, [onKeyDown]);

  return ref;
}

function useClickOutside(onClickOutside: () => void) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClickOutside();
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [onClickOutside]);

  return ref;
}

function mergeRefs<T>(...refs: Array<React.Ref<T>>): React.RefCallback<T> {
  return (el: T) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref && typeof ref === 'object' && 'current' in ref) {
        (ref as React.MutableRefObject<T | null>).current = el;
      }
    });
  };
}
